import type { SetupItem } from "./resources.js";

export interface GeneratedCheck {
  asset_id: string | null;
  cleaning_plan_item_id: string | null;
  origin: "manager" | "plan_generated";
  status: "active" | "archived" | "paused";
}

// Mirrors the worker: only fridges and chillers get a daily check.
const CHECKED_ASSET_TYPES = new Set(["fridge", "chiller"]);

// The frequencies the worker always turns into a schedule. Anything fancier
// is left out here so a frequency the worker skips never looks out of date.
const SIMPLE_FREQUENCIES = new Set([
  "daily",
  "each day",
  "every day",
  "weekly",
  "each week",
  "every week",
  "monthly",
  "each month",
  "every month",
]);

function isActive(item: SetupItem): boolean {
  return (
    !item.archived_at &&
    (item.status === undefined ||
      item.status === null ||
      item.status === "active")
  );
}

// Checks come from the published plan, so fridges and cleaning added or
// retired since then need the plan published again. Any live check counts as
// covering its fridge, whoever made it: republishing never helps there.
export function checksOutOfDate(
  assets: readonly SetupItem[],
  cleaning: readonly SetupItem[],
  checks: readonly GeneratedCheck[],
): number {
  const live = checks.filter((check) => check.status !== "archived");
  // Right after publishing the plan's checks are still being made.
  if (!live.some((check) => check.origin === "plan_generated")) return 0;
  // Editing a plan check (even just its people) currently re-marks it as
  // manager-made, and publishing again would then duplicate it. Until the API
  // keeps the origin, never offer to republish once that may have happened.
  if (
    live.some(
      (check) =>
        check.origin === "manager" &&
        (check.asset_id !== null || check.cleaning_plan_item_id !== null),
    )
  )
    return 0;
  const checkedAssets = new Set(live.flatMap((check) => check.asset_id ?? []));
  const checkedCleaning = new Set(
    live.flatMap((check) => check.cleaning_plan_item_id ?? []),
  );
  const wantedAssets = assets.filter(
    (asset) =>
      isActive(asset) &&
      typeof asset.asset_type === "string" &&
      CHECKED_ASSET_TYPES.has(asset.asset_type),
  );
  const wantedCleaning = cleaning.filter(
    (item) =>
      isActive(item) &&
      item.task_logging_enabled === true &&
      typeof item.frequency === "string" &&
      SIMPLE_FREQUENCIES.has(item.frequency.trim().toLowerCase()),
  );
  const missing =
    wantedAssets.filter((asset) => !checkedAssets.has(asset.id)).length +
    wantedCleaning.filter((item) => !checkedCleaning.has(item.id)).length;
  const wantedAssetIds = new Set(
    assets.filter(isActive).map((asset) => asset.id),
  );
  const wantedCleaningIds = new Set(
    cleaning.filter(isActive).map((item) => item.id),
  );
  const stale = live.filter(
    (check) =>
      check.origin === "plan_generated" &&
      ((check.asset_id !== null && !wantedAssetIds.has(check.asset_id)) ||
        (check.cleaning_plan_item_id !== null &&
          !wantedCleaningIds.has(check.cleaning_plan_item_id))),
  ).length;
  return missing + stale;
}
